import { QueryClient } from '@tanstack/react-query'
import { getAccessToken, refreshAccessToken, forceLogout, isImpersonating, IMPERSONATION_ENDED_EVENT } from '@/lib/tokenRefresh'
import type {
  AirSearchResult, AircraftDetail, Fr24Track, OperatorDetail, AirportPoint, AirportDetail,
  AirHealth, Fr24UsageSummary, WatchRule, WatchRuleInput, AirAlert, RoutesResult,
} from '@/types/airTraffic'

const API_BASE_URL = '/api'

// Freemium credit-nudge events — a lightweight pub/sub via window CustomEvent so any
// component (mounted once, see FreemiumNudgeProvider) can react to usage changes
// without prop-drilling through every page that calls apiClient methods.
export const CREDITS_UPDATE_EVENT = 'sycek:credits-update'
export const CREDITS_EXHAUSTED_EVENT = 'sycek:credits-exhausted'
export interface CreditsUpdateDetail { used: number; limit: number }
// Must match auth_dependency.FREEMIUM_LIMIT_MESSAGE in apps/api exactly — this is how
// the hard-stop nudge distinguishes "freemium credits exhausted" 429s from the
// unrelated per-minute Redis rate-limit 429 (different message, same status code).
const FREEMIUM_LIMIT_MESSAGE = 'Free tier request limit reached. Upgrade your plan to continue.'

function dispatchCreditsUpdate(used: number, limit: number) {
  window.dispatchEvent(new CustomEvent<CreditsUpdateDetail>(CREDITS_UPDATE_EVENT, { detail: { used, limit } }))
}
function dispatchCreditsExhausted() {
  window.dispatchEvent(new CustomEvent(CREDITS_EXHAUSTED_EVENT))
}

export interface Graph {
  nodes: Node[]
  edges: Edge[]
  facets: Record<string, Record<string, number>>
  metadata: Record<string, any>
}

export interface Node {
  id: string
  type: string
  label: string
  props: Record<string, any>
  sentiment?: string
  confidence: number
  created_at?: string
  source?: string
}

export interface Edge {
  id: string
  source: string
  target: string
  relation: string
  weight: number
  props: Record<string, any>
  confidence: number
  created_at?: string
  edge_source?: string
}

export interface SocintSearchRequest {
  query: string
  types?: string[]
  time_from?: string
  time_to?: string
  sources?: string[]
  max_results?: number
  include_sentiment?: boolean
}

export interface RealtimeRule {
  case_id: string
  keywords: string[]
  handles: string[]
  domains: string[]
  platforms: string[]
}

export interface ReportRequest {
  case_id: string
  title: string
  query: string
  results: Graph
  timestamp: string
  metadata: Record<string, any>
  sections?: Record<string, any>
}

export interface ReportResponse {
  url: string
  case_id: string
  title: string
  sentiment_filter?: string
  active: boolean
}

export interface RealtimeEvent {
  id: string
  platform: string
  timestamp: string
  text: string
  entities: any[]
  sentiment: string
  link?: string
  case_id?: string
  rule_id?: string
  metadata: Record<string, any>
}

export interface Case {
  id: string
  title: string
  description: string
  status: string
  created_by: string
  created_at: string
  updated_at: string
  entities: string[]
  tags: string[]
  classification: string
}

export interface PartnershipRequest {
  name: string
  email: string
  company: string
  partnership_type: string
  message?: string
}

export interface TrainingInterestRequest {
  name: string
  email: string
  company?: string
  course_name: string
  message?: string
}

export interface ContactResponse {
  success: boolean
  message: string
}


class ApiClient {
  private baseUrl: string
  private token: string | null = null

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
    this.token = getAccessToken()
  }

  setToken(token: string) {
    this.token = token
    localStorage.setItem('auth_token', token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { responseType?: 'blob' | 'json'; signal?: AbortSignal; timeoutMs?: number } = {},
    _isRetry = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const { responseType, signal: providedSignal, timeoutMs = 600000, ...fetchOptions } = options

    // Re-read on every call (not just the constructor) — a background silent
    // refresh may have swapped in a new token since this client instance was
    // created, and `this.token` would otherwise still be holding the stale one.
    this.token = getAccessToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers as Record<string, string>),
    }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }

    // Use provided signal (from React Query) or create our own with configurable timeout
    let controller: AbortController | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let signal = providedSignal

    if (!signal) {
      controller = new AbortController()
      signal = controller.signal
      timeoutId = setTimeout(() => {
        if (controller && !controller.signal.aborted) {
          controller.abort()
        }
      }, timeoutMs)
    }

    try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
        signal,
    })

      if (timeoutId) {
    clearTimeout(timeoutId)
      }

      // On a 401, try a silent refresh before giving up — this is what stops
      // a session from dying mid-task just because the access token's short
      // (deliberately so) lifetime elapsed while the user was busy. Only one
      // retry: if the refreshed token *still* 401s, the refresh token itself
      // is bad and it's a real logout, not a transient timing issue.
      if (response.status === 401 && !_isRetry && this.token) {
        const newToken = await refreshAccessToken()
        if (newToken) {
          this.token = newToken
          return this.request<T>(endpoint, options, true)
        }
      }

      return await this.handleResponse<T>(response, responseType)
    } catch (error: any) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      // Re-throw abort errors with better message
      if (error.name === 'AbortError') {
        if (providedSignal && providedSignal.aborted) {
          // Request was cancelled by React Query or user
          throw new Error('Request was cancelled')
        } else if (controller && controller.signal.aborted) {
          const mins = Math.round(timeoutMs / 60000)
          throw new Error(`Request timed out - API took too long to respond (over ${mins} minutes)`)
        } else {
          // Unknown abort reason
          throw new Error('Request was aborted')
        }
      }
      throw error
    }
  }

  private async handleResponse<T>(response: Response, responseType?: 'blob' | 'json'): Promise<T> {
    // Freemium nudge: every successful gated call carries the running credit count
    // as response headers (see auth_dependency.consume_credit) — surface it so the
    // welcome/soft-nudge toasts can fire at the right threshold.
    const creditsUsedHeader = response.headers.get('X-Credits-Used')
    const creditsLimitHeader = response.headers.get('X-Credits-Limit')
    if (creditsUsedHeader !== null && creditsLimitHeader !== null) {
      dispatchCreditsUpdate(Number(creditsUsedHeader), Number(creditsLimitHeader))
    }

    if (!response.ok) {
      if (response.status === 401) {
        // Reaching here means the silent-refresh retry in request() either
        // wasn't attempted (no token to begin with) or already failed.
        if (isImpersonating()) {
          // Impersonation tokens are deliberately non-refreshable (see
          // startImpersonation), so a 401 here — natural 15-min expiry, or
          // any other reason — must NOT forceLogout() the whole browser
          // session. Fall back to the admin's own session instead.
          window.dispatchEvent(new Event(IMPERSONATION_ENDED_EVENT))
        } else {
          // This really is an expired/invalid session now.
          forceLogout()
        }
      }

      // For blob responses, try to parse error message
      if (responseType === 'blob') {
        const errorText = await response.text()
        try {
          const errorJson = JSON.parse(errorText)
          throw new Error(errorJson.detail || `HTTP ${response.status}: ${response.statusText}`)
        } catch {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      }
      // Parse error body for detailed message (e.g. 500 returns { detail: "..." })
      const errorText = await response.text()
      try {
        const errorJson = JSON.parse(errorText)
        const detail = errorJson.detail || errorJson.message || `${response.status} ${response.statusText}`
        const message = typeof detail === 'string' ? detail : JSON.stringify(detail)
        // Two independent backends can produce a freemium-exhausted 429: the lifetime
        // credit gate (auth_dependency.consume_credit / breach/search's own check, exact
        // FREEMIUM_LIMIT_MESSAGE) and the separate legacy per-day Redis rate limiter,
        // which instead returns { tier: "freemium", ... } with no matching detail text.
        if (response.status === 429 && (message === FREEMIUM_LIMIT_MESSAGE || errorJson.tier === 'freemium')) {
          dispatchCreditsExhausted()
        }
        throw new Error(message)
      } catch (e) {
        // Re-throw our Error(detail) from parsed JSON; use generic for parse failures
        if (e instanceof Error && !(e instanceof SyntaxError)) throw e
        throw new Error(`API Error: ${response.status} ${response.statusText}`)
      }
    }

    // Handle blob responses
    if (responseType === 'blob') {
      return response.blob() as Promise<T>
    }

    return response.json() as Promise<T>
  }

  // Authentication
  async login(email: string, password: string, turnstileToken?: string, rememberMe = false) {
    const response = await this.request<{ access_token: string; refresh_token?: string; token_type: string; user: any }>('/auth/login-new', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstile_token: turnstileToken, remember_me: rememberMe }),
    })

    this.setToken(response.access_token)
    if (response.refresh_token) {
      localStorage.setItem('refresh_token', response.refresh_token)
    }
    return response
  }

  async register(data: {
    email: string
    password: string
    first_name: string
    last_name: string
    turnstile_token?: string
    invite_token?: string
    invite_code?: string
  }) {
    return this.request('/auth/register-new', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getInviteByToken(inviteToken: string): Promise<{ email: string; first_name: string; last_name: string; role: string; subscription_tier: string }> {
    return this.request(`/auth/invite/${inviteToken}`)
  }

  async getInviteByCode(inviteCode: string): Promise<{ email: string; organization_name: string }> {
    return this.request(`/auth/invite-code/${inviteCode}`)
  }

  async getTurnstileSiteKey() {
    return this.request<{ site_key: string; enabled: boolean }>('/auth/turnstile-site-key')
  }

  async getRegistrationEnabled(): Promise<{ registration_enabled: boolean }> {
    return this.request('/auth/registration-enabled')
  }

  async getCurrentUser() {
    return this.request('/users/me')
  }

  async updateUserProfile(data: {
    email?: string
    first_name?: string
    last_name?: string
  }) {
    return this.request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async updatePassword(data: {
    current_password: string
    new_password: string
  }) {
    return this.request('/users/me/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // API Key Management (legacy internal endpoints)
  async generateApiKey() {
    return this.request('/api/v1/api-keys/generate', {
      method: 'POST',
    })
  }

  async listApiKeys() {
    return this.request('/api/v1/api-keys')
  }

  async revokeApiKey(apiKey: string) {
    return this.request('/api/v1/api-keys/revoke', {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey }),
    })
  }

  // Developer Portal endpoints — public API key management
  async developerCreateKey(): Promise<{ key: string; prefix: string; message: string }> {
    return this.request('/developer/keys', { method: 'POST' })
  }

  async developerListKeys(): Promise<{ keys: { masked: string; prefix: string }[]; count: number; limit: number }> {
    return this.request('/developer/keys')
  }

  async developerRevokeKey(prefix: string): Promise<{ message: string; revoked: boolean }> {
    return this.request(`/developer/keys/${encodeURIComponent(prefix)}`, { method: 'DELETE' })
  }

  async developerUsage(): Promise<{
    credits_total: number
    credits_used: number
    credits_remaining: number | null
    unlimited: boolean
    subscription_tier: string
    endpoint_costs: Record<string, Record<string, number>>
    api_keys_used: number
    api_keys_limit: number
  }> {
    return this.request('/developer/usage')
  }

  // SOCINT
  async socintSearch(request: SocintSearchRequest): Promise<Graph> {
    return this.request<Graph>('/socint/search', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  // Realtime
  async addRealtimeRule(rule: RealtimeRule) {
    return this.request('/realtime/rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    })
  }

  async getRealtimeRules() {
    return this.request('/realtime/rules')
  }

  async deleteRealtimeRule(ruleId: string) {
    return this.request(`/realtime/rules/${ruleId}`, {
      method: 'DELETE',
    })
  }

  createRealtimeWebSocket(): WebSocket {
    // Use the backend URL directly for WebSocket connection
    const wsUrl = 'ws://localhost:8000/realtime/ws'
    return new WebSocket(wsUrl)
  }

  // Reports

  // Cases
  async getCases(): Promise<{cases: Case[], total: number}> {
    return this.request<{cases: Case[], total: number}>('/cases')
  }

  async getCase(caseId: string): Promise<Case> {
    return this.request<Case>(`/cases/${caseId}`)
  }

  async createCase(caseData: Partial<Case>): Promise<Case> {
    return this.request<Case>('/cases', {
      method: 'POST',
      body: JSON.stringify(caseData),
    })
  }

  async updateCase(caseId: string, updates: Partial<Case>): Promise<Case> {
    return this.request<Case>(`/cases/${caseId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async deleteCase(caseId: string): Promise<void> {
    return this.request(`/cases/${caseId}`, {
      method: 'DELETE',
    })
  }

  // GEOINT
  async createAOI(aoiData: any) {
    return this.request('/geoint/aoi', {
      method: 'POST',
      body: JSON.stringify(aoiData),
    })
  }

  async getImagery(aoiId: string) {
    return this.request(`/geoint/imagery/${aoiId}`)
  }

  // BreachINT
  async searchBreaches(query: string, forceRefresh: boolean = false) {
    return this.request('/breach/search', {
      method: 'POST',
      body: JSON.stringify({ query, force_refresh: forceRefresh }),
    })
  }

  /** Caller ID & Facebook lookup (Eyecon/RapidAPI). Rate limit: 5 req/month per key; keys rotated. */
  async getCallerId(countryCode: string, number: string): Promise<{
    status: boolean
    message?: string
    data?: {
      fullName?: string
      b64?: string
      otherNames?: Array<{ name: string; type: string }>
      facebookID?: Record<string, unknown>
      images?: Array<{
        id?: string
        pictures?: { '200'?: string; '600'?: string }
      }>
    }
  }> {
    const params = new URLSearchParams({ country_code: countryCode, number })
    return this.request(`/breach/caller-id?${params.toString()}`, { method: 'GET' })
  }

  async searchLeakOSINT(query: string, _limit: number = 100, _lang: string = 'en') {
    // Use the unified breach search endpoint
    return this.request('/breach/search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    })
  }

  async searchLeakOSINTProxy(_token: string, request: string, _limit: number = 100, _lang: string = 'en') {
    // Use the unified breach search endpoint
    return this.request('/breach/search', {
      method: 'POST',
      body: JSON.stringify({ query: request }),
    })
  }

  async getGmailOSINT(email: string) {
    const encodedEmail = encodeURIComponent(email)
    return this.request(`/breach/gmail-osint/${encodedEmail}`, {
      method: 'GET',
    })
  }

  async getBreachAIInsights(breachData: any) {
    return this.request('/breach/ai-insights', {
      method: 'POST',
      body: JSON.stringify(breachData),
    })
  }

  async getBreachAIEnrichments(breachData: any) {
    return this.request('/breach/ai-enrich', {
      method: 'POST',
      body: JSON.stringify(breachData),
    })
  }

  async pivotEnrich(data: {
    seed: string
    phones: string[]
    emails: string[]
    usernames: string[]
  }): Promise<any> {
    return this.request('/breach/pivot-enrich', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // OFAC Sanctions
  async getSanctionsStats(): Promise<SanctionsStats> {
    return this.request('/sanctions/stats')
  }

  async searchSanctions(params: { q?: string; sdn_type?: string; program?: string; country?: string; page?: number; page_size?: number }): Promise<SanctionsSearchResult> {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.sdn_type) qs.set('sdn_type', params.sdn_type)
    if (params.program) qs.set('program', params.program)
    if (params.country) qs.set('country', params.country)
    if (params.page) qs.set('page', String(params.page))
    if (params.page_size) qs.set('page_size', String(params.page_size))
    return this.request(`/sanctions/search?${qs.toString()}`)
  }

  async getSanctionEntity(uid: number): Promise<SanctionEntity> {
    return this.request(`/sanctions/entity/${uid}`)
  }

  async getSanctionsPrograms(): Promise<{ programs: string[] }> {
    return this.request('/sanctions/programs')
  }

  // Air Intelligence Console (components/air/*). The live picture itself
  // streams over the /api/air-traffic/ws WebSocket (components/air/useAirLive.ts).
  async airSearch(q: string): Promise<AirSearchResult> {
    return this.request(`/air-traffic/search?q=${encodeURIComponent(q)}`)
  }

  /** enrich=true may spend 1 FR24 credit (cached 10 min per aircraft). */
  async airAircraft(hex: string, enrich = false): Promise<AircraftDetail> {
    return this.request(`/air-traffic/aircraft/${encodeURIComponent(hex)}?enrich=${enrich}`)
  }

  /** Full FR24 flight path: 40 credits, only on an explicit click. */
  async airAircraftTrack(hex: string): Promise<Fr24Track> {
    return this.request(`/air-traffic/aircraft/${encodeURIComponent(hex)}/track`)
  }

  /** Origin/destination for up to 60 aircraft: ~1 FR24 credit each, cached 10 min. */
  async airRoutes(hexes: string[]): Promise<RoutesResult> {
    return this.request('/air-traffic/routes', { method: 'POST', body: JSON.stringify({ hexes }) })
  }

  async airOperator(name: string): Promise<OperatorDetail> {
    return this.request(`/air-traffic/operator?name=${encodeURIComponent(name)}`)
  }

  async airAirports(bbox: [number, number, number, number]): Promise<{ airports: AirportPoint[] }> {
    return this.request(`/air-traffic/airports?bbox=${bbox.map((v) => v.toFixed(3)).join(',')}`)
  }

  async airAirport(code: string, radiusKm = 50): Promise<AirportDetail> {
    return this.request(`/air-traffic/airports/${encodeURIComponent(code)}?radius_km=${radiusKm}`)
  }

  async airHealth(): Promise<AirHealth & { fr24: Fr24UsageSummary; atc: { feeds: number; airports: number } }> {
    return this.request('/air-traffic/health')
  }

  async airWatchRules(): Promise<{ rules: WatchRule[] }> {
    return this.request('/air-traffic/watch-rules')
  }

  async airCreateWatchRule(rule: WatchRuleInput): Promise<WatchRule> {
    return this.request('/air-traffic/watch-rules', { method: 'POST', body: JSON.stringify(rule) })
  }

  async airUpdateWatchRule(id: string, patch: Partial<WatchRuleInput>): Promise<WatchRule> {
    return this.request(`/air-traffic/watch-rules/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) })
  }

  async airDeleteWatchRule(id: string) {
    return this.request(`/air-traffic/watch-rules/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  async airAlerts(limit = 50): Promise<{ alerts: AirAlert[]; unread: number }> {
    return this.request(`/air-traffic/alerts?limit=${limit}`)
  }

  async airReadAlert(id: string) {
    return this.request(`/air-traffic/alerts/${encodeURIComponent(id)}/read`, { method: 'POST' })
  }

  async airReadAllAlerts() {
    return this.request('/air-traffic/alerts/read-all', { method: 'POST' })
  }

  async airReport(kind: 'area' | 'airport' | 'aircraft' | 'operator', body: Record<string, unknown>): Promise<Blob> {
    return this.request<Blob>(`/air-traffic/report/${kind}`, {
      method: 'POST',
      body: JSON.stringify(body),
      responseType: 'blob',
      timeoutMs: 120000,
    })
  }

  async airImportLiveAtc(files: File[], lines: string): Promise<{ added: number; updated: number; parsed: number }> {
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    form.append('lines', lines)
    const token = getAccessToken()
    const res = await fetch(`${this.baseUrl}/air-traffic/admin/liveatc-import`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data?.detail || `Import failed (${res.status})`)
    return data
  }

  // Maritime Intelligence methods
  async getMaritimeVessels(request: any) {
    return this.request('/maritime/vessels', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getMaritimeSearch(query: string): Promise<{ vessels: any[]; total_count: number; data_source: string }> {
    return this.request('/maritime/search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    })
  }

  async getMaritimeSummary(request: any) {
    return this.request('/maritime/summary', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getMaritimePorts(request: any) {
    return this.request('/maritime/ports', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getMaritimeThreats(request: any) {
    return this.request('/maritime/threats', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  // WiFi Intelligence methods
  async getWiFiNetworks(request: any) {
    return this.request('/wifi/networks', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getWiFiSummary(request: any) {
    return this.request('/wifi/summary', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getWiFiThreats(request: any) {
    return this.request('/wifi/threats', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getWiFiByLocation(latitude: number, longitude: number, radius: number = 1.0) {
    return this.request(`/wifi/networks/location?latitude=${latitude}&longitude=${longitude}&radius_km=${radius}`)
  }

  async getWiFiBySSID(ssid: string, latitude?: number, longitude?: number) {
    const params = new URLSearchParams({ ssid })
    if (latitude) params.append('latitude', latitude.toString())
    if (longitude) params.append('longitude', longitude.toString())
    return this.request(`/wifi/networks/ssid?${params}`)
  }

  async testWiFiConnection() {
    return this.request('/wifi/test')
  }

  async searchBluetoothDevices(request: any) {
    return this.request('/wifi/bluetooth/search', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async searchCellTowers(request: any) {
    return this.request('/wifi/cell/search', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  // OpenCellID API methods
  async lookupCellTower(params: {
    mcc: number
    mnc: number
    lac: number
    cellid: number
    radio?: string
  }) {
    return this.request('/opencellid/cell/lookup', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async searchOpenCellIDCells(params: {
    latmin: number
    lonmin: number
    latmax: number
    lonmax: number
    mcc?: number
    mnc?: number
    lac?: number
    radio?: string
    limit?: number
    offset?: number
  }) {
    return this.request('/opencellid/cell/search', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async countOpenCellIDCells(params: {
    latmin: number
    lonmin: number
    latmax: number
    lonmax: number
    mcc?: number
    mnc?: number
    lac?: number
    radio?: string
  }) {
    return this.request('/opencellid/cell/count', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async searchAllDevices(params: {
    latrange1: number;
    latrange2: number;
    longrange1: number;
    longrange2: number;
    include_wifi?: boolean;
    include_bluetooth?: boolean;
    include_cell?: boolean;
    results_per_page?: number;
    city?: string;
    country?: string;
  }) {
    return this.request('/wifi/devices/all', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async searchWiFiMapByCitySlug(citySlug: string) {
    return this.request(`/wifi/wifimap/city/${encodeURIComponent(citySlug)}`)
  }

  async searchWiFiMapByCityName(cityName: string, countryCode?: string) {
    const params = new URLSearchParams({ city_name: cityName })
    if (countryCode) params.append('country_code', countryCode)
    return this.request(`/wifi/wifimap/city-name/${encodeURIComponent(cityName)}?${params}`)
  }

  // AI
  async suggestMerges(graphData: Graph) {
    return this.request('/ai/suggest-merges', {
      method: 'POST',
      body: JSON.stringify(graphData),
    })
  }

  async generateEntityNote(entityData: any) {
    return this.request('/ai/entity-note', {
      method: 'POST',
      body: JSON.stringify(entityData),
    })
  }

  // System
  async getSystemSettings() {
    return this.request('/system/settings')
  }

  async getPrecreatedRules() {
    return this.request('/system/rules')
  }

  async createPrecreatedRule(ruleData: any) {
    return this.request('/system/rules', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    })
  }

  async updateSystemSetting(settingName: string, settingValue: any) {
    return this.request('/system/settings', {
      method: 'PUT',
      body: JSON.stringify({ setting_name: settingName, setting_value: settingValue }),
    })
  }

  // Social Media
  // Stream Monitoring API methods
  async createStream(data: { keyword: string; platforms: string[]; fetch_interval_minutes?: number; email_alerts_enabled?: boolean }, signal?: AbortSignal) {
    return this.request('/social/streams', {
      method: 'POST',
      body: JSON.stringify(data),
      signal
    })
  }

  async listStreams(includeInactive: boolean = true, signal?: AbortSignal) {
    return this.request(`/social/streams?include_inactive=${includeInactive}`, {
      method: 'GET',
      signal
    })
  }

  async getStream(streamId: string, signal?: AbortSignal) {
    return this.request(`/social/streams/${streamId}`, {
      method: 'GET',
      signal
    })
  }

  async getStreamPosts(streamId: string, limit: number = 500, signal?: AbortSignal) {
    return this.request(`/social/streams/${streamId}/posts?limit=${limit}`, {
      method: 'GET',
      signal
    })
  }

  async updateStream(streamId: string, data: { keyword?: string; platforms?: string[]; fetch_interval_minutes?: number; email_alerts_enabled?: boolean }, signal?: AbortSignal) {
    return this.request(`/social/streams/${streamId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal
    })
  }

  async activateStream(streamId: string, signal?: AbortSignal) {
    return this.request(`/social/streams/${streamId}/activate`, {
      method: 'POST',
      signal
    })
  }

  async deactivateStream(streamId: string, signal?: AbortSignal) {
    return this.request(`/social/streams/${streamId}/deactivate`, {
      method: 'POST',
      signal
    })
  }

  async deleteStream(streamId: string, signal?: AbortSignal) {
    return this.request(`/social/streams/${streamId}`, {
      method: 'DELETE',
      signal
    })
  }

  async socialSearch(searchData: { query: string; platforms: string[]; count_per_platform?: number; include_marketplace?: boolean; sort_by?: 'relevance' | 'recency' | 'engagement'; cursors?: Record<string, string> }, signal?: AbortSignal) {
    return this.request('/social/search', {
      method: 'POST',
      body: JSON.stringify(searchData),
      signal,
    })
  }

  async getStreamDetections(streamId: string, limit: number = 50, before?: string, signal?: AbortSignal) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (before) params.set('before', before)
    return this.request<{ success: boolean; stream_id: string; detections: any[]; total_returned: number; next_cursor: string | null; has_more: boolean }>(
      `/social/streams/${streamId}/detections?${params}`, { method: 'GET', signal }
    )
  }

  async getStreamCorrelations(streamId: string, status?: string, limit: number = 50, signal?: AbortSignal) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (status) params.set('status', status)
    return this.request<{ success: boolean; stream_id: string; correlations: any[]; total: number }>(
      `/social/streams/${streamId}/correlations?${params}`, { method: 'GET', signal }
    )
  }

  async acknowledgeStreamCorrelation(streamId: string, correlationId: string) {
    return this.request(`/social/streams/${streamId}/correlations/${correlationId}/acknowledge`, { method: 'POST' })
  }

  async getMyCorrelations(status?: string, limit: number = 50, signal?: AbortSignal) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (status) params.set('status', status)
    return this.request<{ success: boolean; correlations: any[]; total: number; unread_count: number }>(
      `/social/correlations?${params}`, { method: 'GET', signal }
    )
  }

  async getLinkedAccounts(platform: string, authorId: string, signal?: AbortSignal) {
    return this.request<{ success: boolean; primary: any; linked_accounts: any[]; cluster_id: string | null; cluster_confidence: number; status?: string }>(
      `/social/authors/${platform}/${encodeURIComponent(authorId)}/linked-accounts`, { method: 'GET', signal }
    )
  }

  async submitIdentityFeedback(clusterId: string, action: 'confirm' | 'reject') {
    return this.request(`/social/authors/linked-accounts/${clusterId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    })
  }

  async translatePost(text: string, targetLang: string = 'en', sourceLang?: string) {
    return this.request<{ translated_text: string; detected_language: string; target_lang: string }>('/social/translate', {
      method: 'POST',
      body: JSON.stringify({ text, target_lang: targetLang, source_lang: sourceLang }),
    })
  }

  mediaProxyUrl(url: string): string {
    if (!url) return url
    return `${this.baseUrl}/social/media-proxy?url=${encodeURIComponent(url)}`
  }

  async analyzePosts(posts: any[]) {
    return this.request<{
      hashtag_network: { nodes: any[]; edges: any[] };
      top_influencers: any[];
      narrative_clusters: any[];
      timeline: Record<string, number>;
    }>('/social/analyze', {
      method: 'POST',
      body: JSON.stringify({ posts }),
    });
  }

  async socialIntelligence(posts: any[], keyword: string) {
    return this.request<{
      keyword: string;
      post_count: number;
      cib: {
        cib_score: number;
        coordination_flag: boolean;
        similar_text_clusters: any[];
        temporal_clusters: any[];
        cadence_suspects: any[];
        summary: string;
      };
      iocs: {
        urls: any[]; domains: any[]; handles: any[];
        emails: any[]; wallets: any[]; phones: any[];
        total_iocs: number;
      };
      geo: {
        location_clusters: any[];
        geojson: any;
        top_country: string | null;
        inference_method: string;
      };
      velocity: {
        surge: boolean;
        z_score?: number;
        current: number;
        baseline_mean: number | null;
        alert_level?: string;
      };
    }>('/social/intelligence', {
      method: 'POST',
      body: JSON.stringify({ posts, keyword }),
    });
  }

  async socialStixExport(query: string, posts: any[], iocs: any, clusters: any[]) {
    const response = await fetch(`${this.baseUrl}/social/stix-export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify({ query, posts, iocs, clusters }),
    });
    if (!response.ok) throw new Error(`STIX export failed: ${response.statusText}`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sycek-stix-${Date.now()}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  async socialActorProfile(authorId: string, posts: any[]) {
    return this.request<{
      author_id: string;
      author: string;
      post_count: number;
      platforms: Record<string, number>;
      total_engagement: number;
      peak_hours: number[];
      cadence_cv: number | null;
      cadence_flag: boolean;
      followers: number;
      verified: boolean;
    }>('/social/actor-profile', {
      method: 'POST',
      body: JSON.stringify({ author_id: authorId, posts }),
    });
  }

  async generateSocialPDF(data: { posts: any[]; query: string; sentimentStats?: any; includeCharts?: boolean; tlp_level?: string; analysis?: any; iocs?: any }) {
    const response = await fetch(`${this.baseUrl}/social/generate-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`PDF generation failed: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sycek-social-report-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  // Enhanced Social Listening
  async socialListeningSearch(searchData: {
    query: string;
    platforms: string[];
    entity_type: string;
    count_per_platform: number;
    include_news: boolean;
    include_market_data: boolean;
    time_range: string;
    sentiment_analysis: boolean;
    trend_analysis: boolean;
  }) {
    return this.request('/social/listening/search', {
      method: 'POST',
      body: JSON.stringify(searchData),
    })
  }

  // Telescope Monitoring
  async searchTelescopeMessages(searchData: {
    keyword: string
    module: string
    page?: number
    limit?: number
  }, signal?: AbortSignal) {
    return this.request('/telescope/search', {
      method: 'POST',
      body: JSON.stringify({
        keyword: searchData.keyword,
        module: searchData.module,
        page: searchData.page || 1,
        limit: searchData.limit || 10
      }),
      signal
    })
  }

  // Telegram Group Scraping
  async scrapeTelegramGroup(groupIdentifier: string, queryType: 'group' | 'channel' = 'group', signal?: AbortSignal) {
    return this.request('/telescope/scrape-group', {
      method: 'POST',
      body: JSON.stringify({
        group_identifier: groupIdentifier,
        query_type: queryType
      }),
      signal,
      timeoutMs: 5 * 60 * 1000,  // 5 min - group export can take a while
    })
  }

  // Cyber Terrorism (GTD - Global Terrorism Database)
  async getCyberTerrorismStatistics() {
    // Stats query can be slow on large GTD datasets (200k+ rows)
    return this.request('/cyber-terrorism/statistics', { timeoutMs: 5 * 60 * 1000 })
  }

  async queryCyberTerrorismIncidents(params: {
    countries?: string[]
    regions?: string[]
    attack_types?: string[]
    target_types?: string[]
    weapon_types?: string[]
    group_names?: string[]
    start_year?: number
    end_year?: number
    min_fatalities?: number
    min_casualties?: number
    limit?: number
    offset?: number
  }) {
    return this.request('/cyber-terrorism/query', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async getCyberTerrorismAnalytics(params: {
    countries?: string[]
    regions?: string[]
    start_year?: number
    end_year?: number
    group_by?: string
  }) {
    return this.request('/cyber-terrorism/analytics', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async getCyberTerrorismAlerts(limit?: number) {
    const url = limit ? `/cyber-terrorism/alerts?limit=${limit}` : '/cyber-terrorism/alerts'
    return this.request(url)
  }

  async loadCyberTerrorismData(filePath?: string) {
    const url = filePath ? `/cyber-terrorism/load?file_path=${encodeURIComponent(filePath)}` : '/cyber-terrorism/load'
    // GTD import can take 15-30+ min for 200k+ rows
    return this.request(url, { method: 'POST', timeoutMs: 30 * 60 * 1000 })
  }

  async createCyberTerrorismMonitor(params: {
    countries?: string[]
    regions?: string[]
    keywords?: string[]
    group_names?: string[]
    severity_threshold?: string
    enabled?: boolean
  }) {
    return this.request('/cyber-terrorism/monitors', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async getSATPTerroristGroups(params?: {
    countries?: string
    regions?: string
    search?: string
    limit?: number
    offset?: number
  }) {
    const sp = new URLSearchParams()
    if (params?.countries) sp.set('countries', params.countries)
    if (params?.regions) sp.set('regions', params.regions)
    if (params?.search) sp.set('search', params.search)
    if (params?.limit != null) sp.set('limit', String(params.limit))
    if (params?.offset != null) sp.set('offset', String(params.offset))
    const q = sp.toString()
    return this.request(`/cyber-terrorism/satp/groups${q ? `?${q}` : ''}`)
  }

  async scrapeSATPTerroristGroups(regions?: string[]) {
    return this.request('/cyber-terrorism/satp/scrape', {
      method: 'POST',
      body: JSON.stringify({ regions: regions ?? null }),
      timeoutMs: 30 * 60 * 1000,  // 30 min - scraping 450+ groups takes time
    })
  }

  // OSINT Playground
  async listPlaygroundCases() {
    return this.request<{ cases: any[]; total: number }>('/playground/cases')
  }

  async createPlaygroundCase(title: string, description?: string) {
    return this.request('/playground/cases', {
      method: 'POST',
      body: JSON.stringify({ title, description }),
    })
  }

  async getPlaygroundCase(caseId: string) {
    return this.request(`/playground/cases/${caseId}`)
  }

  async deletePlaygroundCase(caseId: string) {
    return this.request(`/playground/cases/${caseId}`, { method: 'DELETE' })
  }

  async getPlaygroundGraph(caseId: string) {
    return this.request<{ nodes: { id: string; label: string; entity_type: string; value: string }[]; links: { id: string; source: string; target: string; label: string }[] }>(`/playground/cases/${caseId}/graph`)
  }

  async addPlaygroundEntity(caseId: string, value: string, entityType: string) {
    return this.request(`/playground/cases/${caseId}/entities`, {
      method: 'POST',
      body: JSON.stringify({ value, entity_type: entityType }),
    })
  }

  async extractPlaygroundEntities(text: string) {
    return this.request<{ entities: { entity_type: string; value: string; canonical_value: string }[] }>(
      '/playground/extract',
      { method: 'POST', body: JSON.stringify({ text }) }
    )
  }

  async addPlaygroundEntitiesBulk(
    caseId: string,
    entities: { value: string; entity_type: string }[]
  ) {
    return this.request(`/playground/cases/${caseId}/entities/bulk`, {
      method: 'POST',
      body: JSON.stringify({ entities }),
    })
  }

  async getPlaygroundEntity(caseId: string, entityId: string) {
    return this.request<{
      id: string
      value: string
      entity_type: string
      confidence: string
      evidence: { id: string; source_type: string; raw: any; captured_at: string }[]
    }>(`/playground/cases/${caseId}/entities/${entityId}`)
  }

  async deletePlaygroundEntity(caseId: string, entityId: string) {
    return this.request(`/playground/cases/${caseId}/entities/${entityId}`, { method: 'DELETE' })
  }

  async addPlaygroundRelation(caseId: string, sourceEntityId: string, targetEntityId: string, label: string) {
    return this.request(`/playground/cases/${caseId}/relations`, {
      method: 'POST',
      body: JSON.stringify({ source_entity_id: sourceEntityId, target_entity_id: targetEntityId, label }),
    })
  }

  async getPlaygroundEntityPivots(caseId: string, entityId: string) {
    return this.request<{ pivots: { pivot_id: string; name: string; description: string }[] }>(
      `/playground/cases/${caseId}/entities/${entityId}/pivots`
    )
  }

  async runPlaygroundPivot(caseId: string, entityId: string, pivotId: string) {
    return this.request<{ id: string; status: string; pivot_id: string }>(
      `/playground/cases/${caseId}/pivots/run`,
      {
        method: 'POST',
        body: JSON.stringify({ entity_id: entityId, pivot_id: pivotId }),
      }
    )
  }

  async getPlaygroundJob(jobId: string) {
    return this.request<{ id: string; status: string; result?: any; error?: string }>(
      `/playground/jobs/${jobId}`
    )
  }

  async listPlaygroundCaseJobs(caseId: string) {
    return this.request<{ jobs: { id: string; pivot_id: string; status: string; created_at: string }[] }>(
      `/playground/cases/${caseId}/jobs`
    )
  }

  async reprocessPlaygroundPivot(caseId: string, entityId: string) {
    return this.request<{ entities_added: number; relations_added: number; evidence_processed: number }>(
      `/playground/cases/${caseId}/entities/${entityId}/reprocess-pivot`,
      { method: 'POST' }
    )
  }

  async getCachedSocialResults(query: string) {
    return this.request(`/social/listening/cached/${encodeURIComponent(query)}`)
  }

  async socialListeningHealthCheck() {
    return this.request('/social/listening/health')
  }

  async getSearchHistory() {
    return this.request('/social/listening/history')
  }

  async saveSearchQuery(queryData: any) {
    return this.request('/social/listening/save-query', {
      method: 'POST',
      body: JSON.stringify(queryData),
    })
  }

  // Health
  async healthCheck() {
    return this.request('/health')
  }

  // Report Generation
  async generateReport(reportData: ReportRequest): Promise<ReportResponse> {
    return this.request('/reports/generate', {
      method: 'POST',
      body: JSON.stringify(reportData),
    })
  }

  // Admin Endpoints
  async getSystemLogs(params?: {
    level?: string;
    limit?: number;
    skip?: number;
    start_date?: string;
    end_date?: string;
    user_id?: string;
    endpoint?: string;
  }) {
    const queryParams = new URLSearchParams()
    if (params?.level) queryParams.append('level', params.level)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.skip) queryParams.append('skip', params.skip.toString())
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.user_id) queryParams.append('user_id', params.user_id)
    if (params?.endpoint) queryParams.append('endpoint', params.endpoint)

    const url = `/admin/logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return this.request(url)
  }

  async getUserQueryHistory(
    userId: string,
    params?: {
      limit?: number;
      start_date?: string;
      end_date?: string;
    }
  ) {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    
    const url = `/admin/users/${userId}/queries${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return this.request(url)
  }

  async getCreditUsage(startDate: string, endDate: string) {
    return this.request(`/admin/analytics/credits?start_date=${startDate}&end_date=${endDate}`)
  }

  async getSystemAnalytics() {
    return this.request('/admin/analytics/system')
  }

  async getAllUsers(skip?: number, limit?: number) {
    const queryParams = new URLSearchParams()
    if (skip !== undefined) queryParams.append('skip', skip.toString())
    if (limit !== undefined) queryParams.append('limit', limit.toString())
    
    const url = `/admin/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return this.request(url)
  }

  async updateUserSubscription(data: {
    user_id: string;
    tier: string;
    custom_limit?: number;
  }) {
    return this.request('/admin/subscriptions', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Comprehensive Admin Management
  async adminCreateUser(data: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    role?: string;
    subscription_tier?: string;
    request_limit?: number;
    organization_id?: string;
  }) {
    return this.request('/admin/users/create', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async adminUpdateUser(userId: string, data: {
    email?: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    subscription_tier?: string;
    request_limit?: number;
    requests_used?: number;
    is_active?: boolean;
    organization_id?: string;
  }) {
    return this.request(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async adminDeleteUser(userId: string) {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE',
    })
  }

  async adminSendEmail(userId: string, subject: string, message: string): Promise<{ status: string; message: string }> {
    return this.request(`/admin/users/${userId}/send-email`, {
      method: 'POST',
      body: JSON.stringify({ subject, message })
    })
  }

  async adminSendPasswordReset(userId: string): Promise<{ status: string; message: string; reset_token?: string }> {
    return this.request(`/admin/users/${userId}/send-password-reset`, {
      method: 'POST'
    })
  }

  async adminInviteUser(data: {
    email: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    subscription_tier?: string;
    request_limit?: number;
  }) {
    return this.request('/admin/users/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async adminActivateUser(userId: string) {
    return this.request(`/admin/users/${userId}/activate`, {
      method: 'POST',
    })
  }

  async getAdminSettings(): Promise<{ registration_enabled?: boolean; monitor_enabled?: boolean }> {
    return this.request('/admin/settings')
  }

  async updateAdminSettings(data: { registration_enabled?: boolean; monitor_enabled?: boolean }) {
    return this.request('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getMonitorServiceStatus(): Promise<{ monitor_enabled: boolean; scheduler_running: boolean; ingestor_status: Record<string, any> }> {
    return this.request('/admin/monitor/status')
  }

  async startMonitorService(): Promise<{ status: string; message: string }> {
    return this.request('/admin/monitor/start', { method: 'POST' })
  }

  async stopMonitorService(): Promise<{ status: string; message: string }> {
    return this.request('/admin/monitor/stop', { method: 'POST' })
  }

  // Keyword-monitoring stream admin APIs — cross-user visibility/control,
  // separate from the owner-scoped /social/streams endpoints every user hits
  // for their own streams.
  async getAdminStreams(includeInactive: boolean = true) {
    return this.request<{ success: boolean; streams: any[]; total: number; active_count: number }>(
      `/admin/streams?include_inactive=${includeInactive}`
    )
  }

  async adminActivateStream(streamId: string) {
    return this.request(`/admin/streams/${streamId}/activate`, { method: 'POST' })
  }

  async adminDeactivateStream(streamId: string) {
    return this.request(`/admin/streams/${streamId}/deactivate`, { method: 'POST' })
  }

  // Telegram monitoring admin APIs
  async getAdminTelegramGroups() {
    return this.request<{ groups: any[] }>('/admin/telegram/groups')
  }

  async adminAddTelegramGroups(groups: string[]) {
    return this.request('/admin/telegram/groups', {
      method: 'POST',
      body: JSON.stringify({ groups }),
    })
  }

  // Twitter monitor admin config
  async getAdminTwitterConfig() {
    return this.request<{ keywords: string[] }>('/admin/twitter/config')
  }

  async adminSetTwitterConfig(keywords: string[]) {
    return this.request('/admin/twitter/config', {
      method: 'POST',
      body: JSON.stringify({ keywords }),
    })
  }

  // Twitter monitor feed (public)
  async getMonitorTwitterFeed(limit = 30) {
    const params = new URLSearchParams({ limit: String(limit) })
    return this.request<TwitterFeedResponse>(`/monitor/twitter/feed?${params}`)
  }

  async adminApproveUser(userId: string) {
    return this.request(`/admin/users/${userId}/approve`, { method: 'POST' })
  }

  async adminRejectUser(userId: string) {
    return this.request(`/admin/users/${userId}/reject`, { method: 'POST' })
  }

  async adminSendApprovalEmail(userId: string) {
    return this.request(`/admin/users/${userId}/send-approval-email`, { method: 'POST' })
  }

  async adminDeactivateUser(userId: string) {
    return this.request(`/admin/users/${userId}/deactivate`, {
      method: 'POST',
    })
  }

  async adminAssignCredits(data: {
    user_id: string;
    credits: number;
    reason?: string;
  }) {
    return this.request('/admin/credits/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async adminResetCredits(data: {
    user_id: string;
    reason?: string;
  }) {
    return this.request('/admin/credits/reset', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async adminBulkCreditOperation(data: {
    user_ids: string[];
    credits: number;
    reason?: string;
  }) {
    return this.request('/admin/credits/bulk', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async adminUpdateUserPermissions(userId: string, permissions: string[]) {
    return this.request(`/admin/users/${userId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ user_id: userId, permissions }),
    })
  }

  // --- Admin Panel Redesign ---------------------------------------------

  async getTiersList() {
    return this.request<Record<string, { request_limit: number; modules: string[] }>>('/admin/tiers/list')
  }

  async adminBulkUserOperation(data: {
    user_ids: string[];
    action: 'activate' | 'deactivate' | 'tier_change' | 'delete' | 'approve' | 'reject';
    subscription_tier?: string;
  }) {
    return this.request<{ status: string; updated: string[]; failed: { user_id: string; error: string }[] }>(
      '/admin/users/bulk',
      { method: 'POST', body: JSON.stringify(data) }
    )
  }

  // --- Admin customer-success assistant (Grok chat + confirmed bulk actions) ---

  async adminAssistantChat(history: { role: 'user' | 'assistant'; content: string }[]) {
    return this.request<{
      reply: string
      proposed_actions: { action: 'bulk_approve' | 'bulk_reject'; user_ids: string[]; summary: string }[]
    }>('/admin/assistant/chat', { method: 'POST', body: JSON.stringify({ history }) })
  }

  async adminAssistantExecute(data: { action: 'bulk_approve' | 'bulk_reject'; user_ids: string[] }) {
    return this.request<{ updated: string[]; failed: { user_id: string; error: string }[] }>(
      '/admin/assistant/execute',
      { method: 'POST', body: JSON.stringify(data) }
    )
  }

  async adminGetUserApiKeys(userId: string) {
    return this.request<{
      user_id: string;
      keys_detail: { masked_key: string; label: string | null; created_at: string | null; last_used: string | null }[];
      _key_mapping: Record<string, string>;
    }>(`/admin/users/${userId}/api-keys`)
  }

  async adminRevokeUserApiKey(userId: string, apiKey: string) {
    return this.request<{ status: string; message: string }>(`/admin/users/${userId}/api-keys`, {
      method: 'DELETE',
      body: JSON.stringify({ api_key: apiKey }),
    })
  }

  async adminImpersonateUser(userId: string) {
    return this.request<{
      access_token: string;
      token_type: string;
      expires_in: number;
      impersonated_user: { id: string; email: string; first_name?: string; last_name?: string };
    }>(`/admin/users/${userId}/impersonate`, { method: 'POST' })
  }

  async adminDeleteOrganization(orgId: string) {
    return this.request<{ status: string; message: string }>(`/organizations/${orgId}`, { method: 'DELETE' })
  }

  async adminListSubscriptions(skip = 0, limit = 100) {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) })
    return this.request<{
      subscriptions: {
        user_id: string; email: string; subscription_tier: string;
        dodo_customer_id: string | null; requests_used: number; request_limit: number; is_active: boolean;
      }[];
      total: number; skip: number; limit: number;
    }>(`/admin/subscriptions?${params}`)
  }

  async adminListWebhookEvents(params?: { skip?: number; limit?: number; event_type?: string; status?: string }) {
    const q = new URLSearchParams()
    if (params?.skip) q.append('skip', String(params.skip))
    if (params?.limit) q.append('limit', String(params.limit))
    if (params?.event_type) q.append('event_type', params.event_type)
    if (params?.status) q.append('status', params.status)
    return this.request<{
      events: {
        id: string; event_type: string; dodo_customer_id: string | null; dodo_subscription_id: string | null;
        user_id: string | null; status: string; error: string | null; received_at: string;
      }[];
      total: number; skip: number; limit: number;
    }>(`/admin/subscriptions/webhook-events${q.toString() ? `?${q}` : ''}`)
  }

  async adminGetEmailLog(params?: { skip?: number; limit?: number; to_email?: string; template?: string; success?: boolean }) {
    const q = new URLSearchParams()
    if (params?.skip) q.append('skip', String(params.skip))
    if (params?.limit) q.append('limit', String(params.limit))
    if (params?.to_email) q.append('to_email', params.to_email)
    if (params?.template) q.append('template', params.template)
    if (params?.success !== undefined) q.append('success', String(params.success))
    return this.request<{
      emails: {
        id: string; to_email: string; subject: string; template: string | null;
        triggered_by_user_id: string | null; success: boolean; error: string | null; sent_at: string;
      }[];
      total: number; skip: number; limit: number;
    }>(`/admin/emails/log${q.toString() ? `?${q}` : ''}`)
  }

  async adminBroadcastEmail(data: {
    filter: { subscription_tier?: string; organization_id?: string; user_ids?: string[] };
    subject: string;
    html_body: string;
    confirm?: boolean;
  }) {
    return this.request<
      | { recipient_count: number; sample_recipients: string[]; exceeds_cap: boolean }
      | { sent: string[]; failed: { email: string; error: string }[] }
    >('/admin/emails/broadcast', { method: 'POST', body: JSON.stringify(data) })
  }

  async adminListAmlCases(params?: { status?: string; limit?: number }) {
    const q = new URLSearchParams()
    if (params?.status) q.append('status', params.status)
    if (params?.limit) q.append('limit', String(params.limit))
    return this.request<{ cases: any[]; count: number }>(`/admin/aml/cases${q.toString() ? `?${q}` : ''}`)
  }

  async adminGetAmlStats() {
    return this.request<{ total: number; by_status: Record<string, number> }>('/admin/aml/stats')
  }

  // System Management - Additional methods
  async getSystemRules() {
    return this.request('/system/rules')
  }

  async createSystemRule(data: {
    name: string;
    description?: string;
    keywords?: string[];
    handles?: string[];
    domains?: string[];
    platforms?: string[];
    sentiment_filter?: string;
  }) {
    return this.request('/system/rules', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updatePrecreatedRule(ruleId: string, data: {
    name?: string;
    description?: string;
    keywords?: string[];
    handles?: string[];
    domains?: string[];
    platforms?: string[];
    sentiment_filter?: string;
    active?: boolean;
  }) {
    return this.request(`/system/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deletePrecreatedRule(ruleId: string) {
    return this.request(`/system/rules/${ruleId}`, {
      method: 'DELETE',
    })
  }

  // Test endpoints
  async testPermissions(userId: string, permissions: string[]) {
    return this.request('/admin/test/permissions', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, permissions }),
    })
  }

  async testSystemSettings() {
    return this.request('/admin/test/system-settings')
  }

  async getEmailStatus() {
    return this.request<{
      configured: boolean
      host: string
      port: number
      from: string
      use_ssl: boolean
      use_tls: boolean
      message: string
    }>('/admin/email-status')
  }

  async testEmail(toEmail?: string) {
    return this.request('/admin/test/email', {
      method: 'POST',
      body: JSON.stringify({ to_email: toEmail }),
    })
  }

  // Audit Logs & Compliance
  async getAuditLogs(params: {
    user_id?: string;
    user_email?: string;
    ip_address?: string;
    endpoint?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    skip?: number;
    method?: string;
    status_code?: number;
  }) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    return this.request(`/admin/audit-logs?${queryParams.toString()}`)
  }

  async getComplianceReport(startDate: string, endDate: string, userId?: string) {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (userId) {
      params.append('user_id', userId);
    }
    return this.request(`/admin/compliance-report?${params.toString()}`)
  }

  async cleanupAuditLogs(retentionDays: number = 2555) {
    return this.request('/admin/audit-logs/cleanup', {
      method: 'POST',
      body: JSON.stringify({ retention_days: retentionDays }),
    })
  }

  // Module Access Management
  async getAvailableModules() {
    return this.request('/admin/modules/list')
  }

  async updateUserModules(userId: string, modules: string[]) {
    return this.request(`/admin/users/${userId}/modules`, {
      method: 'PUT',
      body: JSON.stringify({ user_id: userId, modules }),
    })
  }

  // Organization & Team Management
  async getMyOrganizations() {
    return this.request('/organizations')
  }

  async getOrganizationDetails(orgId: string) {
    return this.request(`/organizations/${orgId}`)
  }

  async createOrganization(name: string, description?: string, adminEmail?: string, subscriptionTier?: string) {
    return this.request('/organizations', {
      method: 'POST',
      body: JSON.stringify({ name, description, admin_email: adminEmail, subscription_tier: subscriptionTier }),
    })
  }

  async updateOrganization(orgId: string, updates: { name?: string; description?: string; subscription_tier?: string; request_limit?: number }) {
    return this.request(`/organizations/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async inviteTeamMember(orgId: string, email: string, role: string = 'org_member') {
    return this.request(`/organizations/${orgId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role, organization_id: orgId }),
    })
  }

  async getOrganizationMembers(orgId: string) {
    const details = await this.getOrganizationDetails(orgId);
    // Extract members from organization details
    // Note: This might need to be adjusted based on actual API response
    return (details as any)?.members || [];
  }

  async getOrganizationInvitations(orgId: string) {
    return this.request(`/organizations/${orgId}/invitations`)
  }

  async cancelInvitation(orgId: string, inviteId: string) {
    return this.request(`/organizations/${orgId}/invitations/${inviteId}`, {
      method: 'DELETE',
    })
  }

  async removeTeamMember(orgId: string, userId: string) {
    return this.request(`/organizations/${orgId}/members/${userId}`, {
      method: 'DELETE',
    })
  }

  async updateTeamMemberRole(orgId: string, userId: string, role: string) {
    return this.request(`/organizations/${orgId}/members/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ user_id: userId, organization_id: orgId, role }),
    })
  }

  async transferOwnership(orgId: string, newAdminId: string) {
    return this.request(`/organizations/${orgId}/transfer`, {
      method: 'POST',
      body: JSON.stringify({ new_admin_id: newAdminId }),
    })
  }

  // Admin: Get all organizations
  async getAllOrganizations(skip: number = 0, limit: number = 100) {
    return this.request(`/admin/organizations?skip=${skip}&limit=${limit}`)
  }

  // Email Validation
  async checkEmailTemp(email: string) {
    return this.request(`/auth/check-email?email=${encodeURIComponent(email)}`, {
      method: 'POST',
    })
  }

  // Payment methods
  async createCheckout(tier: 'starter' | 'professional', successUrl?: string, cancelUrl?: string): Promise<{ checkout_id: string; checkout_url: string; expires_at?: string }> {
    return this.request('/payments/checkout', {
      method: 'POST',
      body: JSON.stringify({
        tier,
        ...(successUrl && { success_url: successUrl }),
        ...(cancelUrl && { cancel_url: cancelUrl }),
      }),
    })
  }

  async createAddonCheckout(addonType: 'mcp' | 'social_stream_basic' | 'social_stream_starter' | 'social_stream_pro'): Promise<{ checkout_id: string; checkout_url: string; expires_at?: string }> {
    return this.request(`/payments/checkout/addon?addon_type=${addonType}`, { method: 'POST' })
  }

  async getCheckoutStatus(checkoutId: string): Promise<{ payment_id: string; status: string; amount: number; currency: string; subscription_id?: string }> {
    return this.request(`/payments/checkout/${checkoutId}`)
  }

  async getUserSubscriptions(): Promise<any> {
    return this.request('/payments/subscriptions')
  }

  async cancelSubscription(subscriptionId: string): Promise<any> {
    return this.request(`/payments/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
    })
  }

  async getWhoisData(domain: string): Promise<any> {
    return this.request(`/breach/whois/${encodeURIComponent(domain)}`, {
      method: 'GET',
    })
  }

  async reverseWhoisLookup(
    searchTerm: string,
    searchField: string = 'registrant_email',
    database: string = 'current',
    pageSize: number = 50
  ): Promise<any> {
    const params = new URLSearchParams({
      search_term: searchTerm,
      search_field: searchField,
      database: database,
      page_size: pageSize.toString()
    })
    return this.request(`/breach/reverse-whois?${params.toString()}`, {
      method: 'GET',
    })
  }

  async exportReportPDF(reportData: any): Promise<Blob> {
    return this.request<Blob>('/breach/export-pdf', {
      method: 'POST',
      body: JSON.stringify(reportData),
      headers: {
        'Content-Type': 'application/json',
      },
      responseType: 'blob',
      timeoutMs: 120000, // 2 min - PDF generation can take a while for large reports
    })
  }

  async submitPartnershipRequest(data: PartnershipRequest): Promise<ContactResponse> {
    const response = await fetch(`${this.baseUrl}/contact/partnership`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to submit partnership request' }))
      throw new Error(error.detail || 'Failed to submit partnership request')
    }
    
    return response.json()
  }

  async submitTrainingInterest(data: TrainingInterestRequest): Promise<ContactResponse> {
    const response = await fetch(`${this.baseUrl}/contact/training-interest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to submit training interest' }))
      throw new Error(error.detail || 'Failed to submit training interest')
    }
    
    return response.json()
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/auth/forgot-password?email=${encodeURIComponent(email)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to request password reset' }))
      throw new Error(error.detail || 'Failed to request password reset')
    }
    
    return response.json()
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, new_password: newPassword }),
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to reset password' }))
      throw new Error(error.detail || 'Failed to reset password')
    }
    
    return response.json()
  }

  // Event Investigation (twitterapi.io)
  async startEventInvestigation(data: {
    post_id_or_url: string;
    max_reply_pages?: number;
    max_quote_pages?: number;
    max_retweeter_pages?: number;
    include_thread?: boolean;
    force_refresh?: boolean;
  }) {
    return this.request('/twitter/investigate/event', {
      method: 'POST',
      body: JSON.stringify(data),
      timeoutMs: 5 * 60 * 1000,
    })
  }

  async runEventPhase(investigationId: string, phase: number) {
    return this.request(`/twitter/investigate/event/${investigationId}/phase/${phase}`, {
      method: 'POST',
      timeoutMs: 5 * 60 * 1000,
    })
  }

  async runEventBotDetection(investigationId: string) {
    return this.request(`/twitter/investigate/event/${investigationId}/bot-detection`, {
      method: 'POST',
      timeoutMs: 3 * 60 * 1000,
    })
  }

  async getEventInvestigation(investigationId: string) {
    return this.request(`/twitter/investigate/event/${investigationId}`)
  }

  async listEventInvestigations(limit = 50) {
    return this.request(`/twitter/investigate/event/list?limit=${limit}`)
  }

  async deleteEventInvestigation(investigationId: string) {
    return this.request(`/twitter/investigate/event/${investigationId}`, {
      method: 'DELETE',
    })
  }

  async findSimilarNarratives(investigationId: string) {
    return this.request(`/twitter/investigate/event/${investigationId}/similar-narratives`, {
      method: 'POST',
    })
  }

  async getEventInvestigationPdfUrl(investigationId: string): Promise<string> {
    const token = localStorage.getItem('auth_token');
    return `/api/twitter/investigate/event/${investigationId}/report/pdf?token=${token}`;
  }

  async getEventGraph(investigationId: string) {
    return this.request(`/twitter/enrichment/graph/${investigationId}`)
  }

  async getEventMetrics(investigationId: string) {
    return this.request(`/twitter/enrichment/metrics/${investigationId}`)
  }

  async runEnhancedUserInvestigation(data: {
    username: string;
    include_followers?: boolean;
    include_followings?: boolean;
    include_tweets?: boolean;
    max_followers?: number;
    max_followings?: number;
    max_tweets?: number;
    force_refresh?: boolean;
  }) {
    return this.request('/twitter/investigate/enhanced', {
      method: 'POST',
      body: JSON.stringify(data),
      timeoutMs: 5 * 60 * 1000,
    })
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/auth/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to verify email' }))
      throw new Error(error.detail || 'Failed to verify email')
    }
    
    return response.json()
  }

  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to resend verification email' }))
      throw new Error(error.detail || 'Failed to resend verification email')
    }
    
    return response.json()
  }

  // ── Monitor Dashboard ─────────────────────────────────────

  async getMonitorConfig() {
    return this.request<MonitorConfig>('/monitor/config')
  }

  async getMonitorEvents(params: MonitorEventsParams = {}) {
    const qs = new URLSearchParams()
    if (params.domains) qs.set('domains', params.domains)
    if (params.event_types) qs.set('event_types', params.event_types)
    if (params.severities) qs.set('severities', params.severities)
    if (params.region) qs.set('region', params.region)
    if (params.country_code) qs.set('country_code', params.country_code)
    if (params.time_from) qs.set('time_from', params.time_from)
    if (params.time_to) qs.set('time_to', params.time_to)
    if (params.search) qs.set('search', params.search)
    if (params.page) qs.set('page', String(params.page))
    if (params.page_size) qs.set('page_size', String(params.page_size))
    if (params.sort_by) qs.set('sort_by', params.sort_by)
    if (params.sort_order) qs.set('sort_order', params.sort_order)
    const query = qs.toString()
    return this.request<MonitorEventsResponse>(`/monitor/events${query ? '?' + query : ''}`)
  }

  async getMonitorEvent(eventId: string) {
    return this.request<MonitorEventItem>(`/monitor/events/${eventId}`)
  }

  async getMonitorStats() {
    return this.request<MonitorStats>('/monitor/stats')
  }

  async triggerMonitorIngestion(source?: string) {
    const qs = source ? `?source=${source}` : ''
    return this.request<{ status: string; results: Record<string, number> }>(`/monitor/ingest${qs}`, { method: 'POST' })
  }

  async getMonitorIngestionStatus() {
    return this.request<Record<string, MonitorIngestionStatus>>('/monitor/ingest/status')
  }

  // ── Monitor Phase 2: Entities, Escalation ──────────────────

  async getMonitorEntities(params: { entity_types?: string; country_code?: string; search?: string; limit?: number } = {}) {
    const qs = new URLSearchParams()
    if (params.entity_types) qs.set('entity_types', params.entity_types)
    if (params.country_code) qs.set('country_code', params.country_code)
    if (params.search) qs.set('search', params.search)
    if (params.limit) qs.set('limit', String(params.limit))
    const query = qs.toString()
    return this.request<MonitorEntitiesResponse>(`/monitor/entities${query ? '?' + query : ''}`)
  }

  async getTopThreatActors(limit: number = 10) {
    return this.request<MonitorEntityItem[]>(`/monitor/entities/threat-actors?limit=${limit}`)
  }

  async getActiveRansomwareGroups(limit: number = 10) {
    return this.request<MonitorEntityItem[]>(`/monitor/entities/ransomware-groups?limit=${limit}`)
  }

  async getMonitorEscalation() {
    return this.request<EscalationData>('/monitor/escalation')
  }

  async getCorrelationSignals(hours: number = 24) {
    return this.request<CorrelationSignalsResponse>(`/monitor/correlation/signals?hours=${hours}`)
  }

  async getCountryIntel(countryCode: string, hours: number = 168) {
    return this.request<CountryIntelData>(`/monitor/country/${countryCode}/intel?hours=${hours}`)
  }

  async getAnomalies(hours: number = 24) {
    return this.request<AnomalyAlertsResponse>(`/monitor/anomalies?hours=${hours}`)
  }

  /** Phase 3: Universal search (events + entities by keyword, actor, CVE, country). */
  async getMonitorSearch(q: string, eventsLimit = 15, entitiesLimit = 10) {
    const params = new URLSearchParams({ q: q.trim(), events_limit: String(eventsLimit), entities_limit: String(entitiesLimit) })
    return this.request<MonitorSearchResponse>(`/monitor/search?${params}`)
  }

  /** Live AIS vessels for Maritime layer (bbox optional; default global). */
  async getMonitorLiveVessels(bbox?: { south: number; west: number; north: number; east: number }, limit = 200) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (bbox) {
      params.set('south', String(bbox.south))
      params.set('west', String(bbox.west))
      params.set('north', String(bbox.north))
      params.set('east', String(bbox.east))
    }
    return this.request<MonitorLiveVesselsResponse>(`/monitor/live/vessels?${params}`)
  }

  /** Live aircraft for Flights layer (bbox optional; default global). */
  async getMonitorLiveFlights(bbox?: { south: number; west: number; north: number; east: number }, limit = 300) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (bbox) {
      params.set('south', String(bbox.south))
      params.set('west', String(bbox.west))
      params.set('north', String(bbox.north))
      params.set('east', String(bbox.east))
    }
    return this.request<MonitorLiveFlightsResponse>(`/monitor/live/flights?${params}`)
  }

  /** GPS jamming data (H3 cells) from gpswise.aero. */
  async getMonitorGpsJamming(periodStart: string, periodEnd: string) {
    const params = new URLSearchParams({ period_start: periodStart, period_end: periodEnd })
    return this.request<GpsJamJammingResponse>(`/monitor/gpsjam/jamming?${params}`)
  }

  /** GPS spoofing GeoJSON (flight segments) from gpswise.aero. */
  async getMonitorGpsSpoofing(startTime: string, endTime: string) {
    const params = new URLSearchParams({ start_time: startTime, end_time: endTime })
    return this.request<GpsJamSpoofingGeoJSON>(`/monitor/gpsjam/spoofing?${params}`)
  }

  /** Prediction markets snapshot from Polymarket (via backend proxy). */
  async getMonitorPolymarketMarkets() {
    return this.request<PolymarketMarketsResponse>('/monitor/polymarket/markets')
  }

  /** Stock market indices snapshot (S&P 500, Nasdaq, Dow). */
  async getMonitorStockSummary() {
    return this.request<StockSummaryResponse>('/monitor/stocks/summary')
  }

  /** Cloudflare Radar outages / anomalies snapshot. */
  async getMonitorCloudflareOutages() {
    return this.request<CloudflareOutagesResponse>('/monitor/outages/cloudflare')
  }

  /** Gamma irradiator facilities (IAEA DIIF) */
  async getGammaIrradiators() {
    return this.request<GammaIrradiatorsResponse>('/monitor/gamma-irradiators')
  }

  /** Curated global military bases */
  async getMilitaryBases() {
    return this.request<MilitaryBasesResponse>('/monitor/bases/curated')
  }

  /** Curated world news headlines for footer ticker. */
  async getMonitorNews() {
    return this.request<MonitorNewsResponse>('/monitor/news/rss')
  }

  /** Live feed of messages from monitored Telegram groups/channels (requires auth). */
  /** Public Telegram live feed (no auth). Use for monitor dashboard for all users. */
  async getMonitorTelegramFeed(limitPerGroup = 20) {
    const params = new URLSearchParams({ limit_per_group: String(limitPerGroup) })
    return this.request<TelegramFeedResponse>(`/monitor/telegram/feed?${params}`)
  }

  async seedMonitorEntities() {
    return this.request<{ status: string; results: Record<string, any> }>('/monitor/entities/seed', { method: 'POST' })
  }

  // ── Topic Stream (SSE) ───────────────────────────────────────────────────
  // Returns an EventSource connected to the topic monitor SSE endpoint.
  // The caller must call .close() when done.
  createTopicStream(opts: {
    topic: string
    platforms: string[]
    duration: number
    lang?: string      // BCP-47 language code e.g. "hi", "ar", "ja"
    region?: string    // ISO-3166-1 alpha-2 e.g. "in", "jp", "eg"
    onInit: (data: any) => void
    onPosts: (data: any) => void
    onHeartbeat: (data: any) => void
    onDone: (data: any) => void
    onError?: (err: Event) => void
  }): EventSource {
    const token = localStorage.getItem('auth_token') || ''
    const params = new URLSearchParams({
      topic:     opts.topic,
      platforms: opts.platforms.join(','),
      duration:  String(opts.duration),
      token,
    })
    if (opts.lang)   params.set('lang', opts.lang)
    if (opts.region) params.set('region', opts.region)
    const url = `${this.baseUrl}/social/topic-stream?${params.toString()}`
    const es = new EventSource(url)

    es.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data)
        switch (msg.type) {
          case 'init':      opts.onInit(msg);      break
          case 'posts':     opts.onPosts(msg);     break
          case 'heartbeat': opts.onHeartbeat(msg); break
          case 'done':      opts.onDone(msg);      break
        }
      } catch { /* ignore malformed */ }
    }

    if (opts.onError) {
      es.onerror = opts.onError
    }

    return es
  }
}

// ── Monitor Dashboard types ──────────────────────────────────

export interface MonitorConfig {
  data_delay_minutes: number
  max_zoom: number
  max_events_per_request: number
  available_domains: string[]
  available_event_types: string[]
  available_severities: string[]
  available_regions: string[]
}

export interface MonitorEventItem {
  id: string
  event_type: string
  domain: string
  title: string
  description: string
  severity: string
  severity_score: number
  latitude: number | null
  longitude: number | null
  country_code: string | null
  country_name: string | null
  region: string | null
  source: string
  source_url: string | null
  tags: string[]
  actors: string[]
  timestamp: string
  ingested_at: string
  metadata: Record<string, any>
}

export interface MonitorEventsResponse {
  events: MonitorEventItem[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export interface MonitorEventsParams {
  domains?: string
  event_types?: string
  severities?: string
  region?: string
  country_code?: string
  time_from?: string
  time_to?: string
  search?: string
  page?: number
  page_size?: number
  sort_by?: string
  sort_order?: string
}

export interface MonitorStats {
  total_events: number
  events_last_24h: number
  events_by_domain: Record<string, number>
  events_by_severity: Record<string, number>
  events_by_type: Record<string, number>
  top_countries: { code: string; name: string; count: number }[]
  last_ingestion: string | null
}

export interface MonitorIngestionStatus {
  source: string
  last_run: string | null
  events_ingested: number
  status: string
  error: string | null
}

// Phase 2 types

export interface MonitorSearchResponse {
  events: MonitorEventItem[]
  entities: MonitorEntityItem[]
  total_events: number
  total_entities: number
}

export interface MonitorLiveVessel {
  mmsi: string
  name: string | null
  latitude: number
  longitude: number
  speed: number | null
  course: number | null
  destination: string | null
  status: string | null
}

export interface MonitorLiveVesselsResponse {
  vessels: MonitorLiveVessel[]
  data_source: string | null
  total_count?: number
  error?: string
}

export interface MonitorLiveAircraft {
  icao24: string
  callsign: string | null
  latitude: number
  longitude: number
  velocity: number | null
  true_track: number | null
  baro_altitude: number | null
  origin_country: string | null
}

export interface MonitorLiveFlightsResponse {
  aircraft: MonitorLiveAircraft[]
  total_count: number
  error?: string
}

export interface GpsJamJammingCell {
  id: string
  h3_indices: string[]
  altitude: string
  n_unique_ac: number
  n_good: number
  n_bad: number
  ratio_bad: number
}

export interface GpsJamJammingResponse {
  period_start: string
  period_end: string
  data: GpsJamJammingCell[]
}

export interface GpsJamSpoofingGeoJSON {
  type: 'FeatureCollection'
  features: Array<{
    type: 'Feature'
    geometry: { type: 'LineString'; coordinates: [number, number][] }
    properties: Record<string, unknown>
  }>
}

export interface PolymarketMarketSummary {
  id: string
  question: string
  category: string | null
  endDate: string | null
  probability: number | null
  outcome: string | null
  volume24hr: number
}

export interface PolymarketMarketsResponse {
  markets: PolymarketMarketSummary[]
  error?: string
}

export interface StockIndexSummary {
  symbol: string
  name: string
  price: number
  change: number
  changePercent: number
  currency: string
}

export interface StockSummaryResponse {
  indices: StockIndexSummary[]
}

export interface CloudflareOutagesResponse {
  map: Record<string, any> | null
  outages: Record<string, any> | null
  traffic: Record<string, any> | null
}

export interface GammaIrradiatorFacility {
  id: string
  city: string
  country: string
  lat: number
  lon: number
}

export interface GammaIrradiatorsResponse {
  facilities: GammaIrradiatorFacility[]
  error?: string
}

export interface MilitaryBase {
  id: string
  name: string
  lat: number
  lon: number
  type: string
  country: string
  arm?: string
  status?: string
  description?: string
}

export interface MilitaryBasesResponse {
  bases: MilitaryBase[]
  error?: string
}

export interface MonitorNewsItem {
  title: string
  link: string
  published: string
  source: string
  category: string
}

export interface MonitorNewsResponse {
  items: MonitorNewsItem[]
}

export interface TelegramFeedMessage {
  id?: string | number
  date?: string
  text?: string
  message?: string
  content?: string
  group_username?: string
  from?: Record<string, unknown>
  [key: string]: unknown
}

export interface TelegramFeedResponse {
  messages: TelegramFeedMessage[]
}

export interface TwitterFeedMedia {
  url: string
  type: 'photo' | 'video' | 'animated_gif'
  video_url?: string
}

export interface TwitterFeedTweet {
  id?: string
  text?: string
  created_at?: string
  author?: {
    name?: string
    username?: string
    profile_image?: string
  }
  metrics?: {
    likes?: number
    retweets?: number
    replies?: number
  }
  media?: TwitterFeedMedia[]
}

export interface TwitterFeedResponse {
  tweets: TwitterFeedTweet[]
  query: string
}

export interface MonitorEntityItem {
  id: string
  entity_type: string
  name: string
  aliases: string[]
  description: string
  country_code: string | null
  country_name: string | null
  tags: string[]
  source: string
  source_url: string | null
  metadata: Record<string, any>
  first_seen: string
  last_seen: string
  event_count: number
  is_active: boolean
}

export interface MonitorEntitiesResponse {
  entities: MonitorEntityItem[]
  total: number
}

export interface CountryScoreItem {
  country_code: string
  country_name: string
  instability_score: number
  conflict_density: number
  cyber_density: number
  infra_outages: number
  sanctions_weight: number
  telegram_anomaly: number
  event_count_24h: number
  last_updated: string
}

export interface CyberEscalationItem {
  global_score: number
  kev_spike: number
  ransomware_activity: number
  ioc_surge: number
  apt_chatter: number
  recent_critical_events: number
  trend: string
  last_updated: string
}

export interface EscalationData {
  country_scores: CountryScoreItem[]
  cyber_escalation: CyberEscalationItem
  composite_score: number
  geo_score: number
  level: string
}

export interface CorrelationSignal {
  country_code: string
  country_name: string
  domains_active: string[]
  signal_level: string
  event_count: number
  top_severity: string
  example_events: string[]
  timestamp: string
}

export interface CorrelationSignalsResponse {
  signals: CorrelationSignal[]
}

export interface CountryIntelData {
  country_code: string
  country_name: string
  total_events: number
  events_by_domain: Record<string, number>
  events_by_severity: Record<string, number>
  recent_events: Array<{
    title: string
    event_type: string
    severity: string
    timestamp: string
    source: string
  }>
  active_actors: string[]
  top_event_types: Record<string, number>
  instability_score: number
  correlation_signals: Array<{
    domains_active: string[]
    signal_level: string
    event_count: number
  }>
  anomalies: Array<{
    entity: string
    change_pct: number
    alert_level: string
  }>
}

export interface AnomalyAlert {
  entity: string
  entity_type: string
  current_count: number
  previous_count: number
  change_pct: number
  alert_level: string
}

export interface AnomalyAlertsResponse {
  anomalies: AnomalyAlert[]
}

// OFAC Sanctions types
export interface SanctionEntity {
  uid: number
  name: string
  sdn_type: string
  programs: string[]
  aliases: { name: string; type: string; strength: string }[]
  addresses: Record<string, string>[]
  ids: Record<string, string>[]
  nationalities: string[]
  countries: string[]
  date_of_birth: string
  place_of_birth: string
  title: string
  remarks: string
  publish_date?: string
}

export interface SanctionsSearchResult {
  results: SanctionEntity[]
  total: number
  page: number
  page_size: number
  has_more: boolean
}

export interface SanctionsStats {
  total: number
  by_type: Record<string, number>
  top_programs: { program: string; count: number }[]
  top_countries: { country: string; count: number }[]
  publish_date: string | null
}

export const apiClient = new ApiClient()
export const queryClient = new QueryClient()